import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

export interface StaggeredMenuItem {
    label: string;
    ariaLabel: string;
    link: string;
    onClick?: () => void;
    subItems?: StaggeredMenuItem[];
    isHeader?: boolean;
    className?: string;
}

export interface StaggeredMenuSocialItem {
    label: string;
    link: string;
    onClick?: () => void;
}

export interface StaggeredMenuProps {
    position?: 'left' | 'right';
    colors?: string[];
    items?: StaggeredMenuItem[];
    socialItems?: StaggeredMenuSocialItem[];
    displaySocials?: boolean;
    displayItemNumbering?: boolean;
    className?: string;
    logoUrl?: string;
    menuButtonColor?: string;
    openMenuButtonColor?: string;
    accentColor?: string;
    isFixed?: boolean;
    changeMenuColorOnOpen?: boolean;
    onMenuOpen?: () => void;
    onMenuClose?: () => void;
}

const FALLBACK_COLORS = ['#ffffff', '#f4f4f5'];

export const StaggeredMenu: React.FC<StaggeredMenuProps> = ({
    position = 'right',
    colors = FALLBACK_COLORS,
    items = [],
    socialItems = [],
    displaySocials = true,
    displayItemNumbering = true,
    className,
    logoUrl,
    menuButtonColor = '#544023',
    openMenuButtonColor = '#f8f6f1',
    accentColor = '#4338ca',
    isFixed = false,
    changeMenuColorOnOpen = true,
    onMenuOpen,
    onMenuClose,
}: StaggeredMenuProps) => {
    const [open, setOpen] = useState(false);
    const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

    const palette = useMemo(() => (colors && colors.length ? colors : FALLBACK_COLORS), [colors]);

    const panelBackground = useMemo(() => {
        if (!palette.length) {
            return undefined;
        }

        if (palette.length === 1) {
            return palette[0];
        }

        const angle = position === 'right' ? '140deg' : '220deg';
        return `linear-gradient(${angle}, ${palette.join(', ')})`;
    }, [palette, position]);

    const buttonColor = open && changeMenuColorOnOpen ? openMenuButtonColor : menuButtonColor;

    const toggleMenu = () => {
        setOpen((previous) => {
            const next = !previous;
            if (next) {
                onMenuOpen?.();
            } else {
                onMenuClose?.();
                setExpandedItems({}); // Reset expanded state on close
            }
            return next;
        });
    };

    const closeMenu = () => {
        setOpen(false);
        setExpandedItems({});
    };

    const toggleItem = (label: string) => {
        setExpandedItems((prev) => ({
            ...prev,
            [label]: !prev[label],
        }));
    };

    const handleItemClick = (item: StaggeredMenuItem) => (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        if (item.subItems && item.subItems.length > 0) {
            event.preventDefault();
            toggleItem(item.label);
            return;
        }

        if (item.onClick) {
            event.preventDefault();
            item.onClick();
        }
        closeMenu();
    };

    const handleSocialClick = (
        item: StaggeredMenuSocialItem,
    ) => (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        if (item.onClick) {
            event.preventDefault();
            item.onClick();
        }
        closeMenu();
    };

    const chipClasses =
        'inline-flex items-center rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:text-slate-900';

    return (
        <div className={cn(isFixed ? 'fixed inset-0 z-50' : 'relative z-50', className)}>
            <button
                type="button"
                onClick={toggleMenu}
                aria-expanded={open}
                aria-label={open ? 'Close menu' : 'Open menu'}
                className={cn(
                    'inline-flex h-10 w-10 md:h-12 md:w-12 ml-4 items-center justify-center rounded-full border border-slate-200 bg-white text-primary shadow-input shadow-sm transition hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                    open && 'ring-2 ring-primary/30',
                )}
                style={{ color: buttonColor }}
            >
                <span className="sr-only">Toggle navigation</span>
                <span className="relative flex h-5 w-5 items-center justify-center">
                    <span
                        className={cn(
                            'absolute h-[2px] w-full bg-current transition-transform duration-300',
                            open ? 'translate-y-0 rotate-45' : '-translate-y-1.5',
                        )}
                    />
                    <span
                        className={cn(
                            'absolute h-[2px] w-full bg-current transition-opacity duration-300',
                            open ? 'opacity-0' : 'opacity-100',
                        )}
                    />
                    <span
                        className={cn(
                            'absolute h-[2px] w-full bg-current transition-transform duration-300',
                            open ? 'translate-y-0 -rotate-45' : 'translate-y-1.5',
                        )}
                    />
                </span>
            </button>

            <div
                className={cn(
                    'absolute top-full mt-4 w-[85vw] max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/10 transition-all duration-300',
                    position === 'right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
                    open ? 'pointer-events-auto translate-y-0 scale-100 opacity-100' : 'pointer-events-none -translate-y-2 scale-95 opacity-0',
                )}
                style={{ background: panelBackground, '--sm-accent-color': accentColor } as React.CSSProperties}
            >

                <div className="flex flex-col gap-6 px-6 pb-6 max-h-[80vh] overflow-y-auto">
                    <ul className="flex flex-col gap-4 pt-6">
                        {items.length ? (
                            items.map((item, index) => {
                                const isExpanded = expandedItems[item.label];
                                const hasSubItems = item.subItems && item.subItems.length > 0;

                                return (
                                    <li key={`${item.label}-${index}`} className="flex flex-col">
                                        <div className="flex items-center justify-between">
                                            {hasSubItems ? (
                                                <div className="flex w-full items-center justify-between">
                                                    <a
                                                        href={item.link}
                                                        onClick={(e) => {
                                                            if (item.onClick) {
                                                                e.preventDefault();
                                                                item.onClick();
                                                            }
                                                            closeMenu();
                                                        }}
                                                        className="group flex flex-1 items-center gap-3 text-2xl font-semibold uppercase tracking-tight text-bg-primary transition-colors hover:text-[var(--sm-accent-color,#4338ca)]"
                                                        aria-label={item.ariaLabel}
                                                    >
                                                        {displayItemNumbering && (
                                                            <span className="text-sm font-medium text-slate-500">
                                                                {String(index + 1).padStart(2, '0')}
                                                            </span>
                                                        )}
                                                        <span>{item.label}</span>
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            toggleItem(item.label);
                                                        }}
                                                        className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-slate-100"
                                                        aria-label={isExpanded ? "Collapse menu" : "Expand menu"}
                                                        aria-expanded={isExpanded}
                                                    >
                                                        <ChevronDown
                                                            className={cn(
                                                                "h-6 w-6 text-primary transition-transform duration-300",
                                                                isExpanded ? "rotate-180" : "rotate-0"
                                                            )}
                                                        />
                                                    </button>
                                                </div>
                                            ) : (
                                                <a
                                                    href={item.link}
                                                    onClick={handleItemClick(item)}
                                                    className="group flex w-full items-center justify-between text-2xl font-semibold uppercase tracking-tight text-bg-primary transition-colors hover:text-[var(--sm-accent-color,#4338ca)]"
                                                    aria-label={item.ariaLabel}
                                                >
                                                    <span className="flex items-center gap-3">
                                                        {displayItemNumbering && (
                                                            <span className="text-sm font-medium text-slate-500">
                                                                {String(index + 1).padStart(2, '0')}
                                                            </span>
                                                        )}
                                                        <span>{item.label}</span>
                                                    </span>
                                                </a>
                                            )}
                                        </div>

                                        {hasSubItems && (
                                            <div
                                                className={cn(
                                                    "grid transition-all duration-300 ease-in-out",
                                                    isExpanded ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0 mt-0"
                                                )}
                                            >
                                                <div className="overflow-hidden">
                                                    <ul className="flex flex-col gap-3 pl-8 border-l-2 border-slate-100 ml-2">
                                                        {item.subItems!.map((subItem, subIndex) => (
                                                            <li key={`${subItem.label}-${subIndex}`}>
                                                                {subItem.isHeader ? (
                                                                    <div className={cn("block text-xs font-bold uppercase text-slate-400 mt-2 mb-1", subItem.className)}>
                                                                        {subItem.label}
                                                                    </div>
                                                                ) : (
                                                                    <a
                                                                        href={subItem.link}
                                                                        onClick={handleItemClick(subItem)}
                                                                        className={cn(
                                                                            "block text-lg font-medium text-primary transition-colors hover:text-[var(--sm-accent-color,#4338ca)]",
                                                                            subItem.className
                                                                        )}
                                                                    >
                                                                        {subItem.label}
                                                                    </a>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                );
                            })
                        ) : (
                            <li className="text-sm text-slate-500">No menu items</li>
                        )}
                    </ul>

                    {displaySocials && socialItems.length > 0 && (
                        <div className="space-y-3 border-t border-white/10 pt-4">
                            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-accent/200">Languages</h3>
                            <div className="flex flex-wrap gap-2">
                                {socialItems.map((item, index) => (
                                    item.onClick ? (
                                        <button
                                            key={`${item.label}-${index}`}
                                            type="button"
                                            onClick={handleSocialClick(item)}
                                            className={chipClasses}
                                        >
                                            {item.label}
                                        </button>
                                    ) : (
                                        <a
                                            key={`${item.label}-${index}`}
                                            href={item.link}
                                            onClick={handleSocialClick(item)}
                                            rel={item.link.startsWith('#') ? undefined : 'noopener noreferrer'}
                                            target={item.link.startsWith('#') ? undefined : '_blank'}
                                            className={chipClasses}
                                        >
                                            {item.label}
                                        </a>
                                    )
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StaggeredMenu;
